// CameraGrid Component - 2ECOMP Fleet Management
// Multi-camera grid display with responsive layout

import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
  Box,
  Grid,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  Paper,
  Fade,
} from '@mui/material';
import { styled, alpha, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import GridViewIcon from '@mui/icons-material/GridView';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ViewComfyIcon from '@mui/icons-material/ViewComfy';
import ViewArrayIcon from '@mui/icons-material/ViewArray';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import VideocamOffIcon from '@mui/icons-material/VideocamOff';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';

import CameraCard from './CameraCard';
import JessibucaPlayer from './JessibucaPlayer';
import { GRID_LAYOUTS } from '../utils/cameraConstants';
import { useVideoCommand } from '../hooks/useVideoCommand';

// Styled Components
const GridContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: theme.palette.background.default,
}));

const GridToolbar = styled(Paper)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(1, 2),
  marginBottom: theme.spacing(1),
  borderRadius: 0,
}));

const GridContent = styled(Box)(({ theme }) => ({
  flex: 1,
  overflow: 'auto',
  padding: theme.spacing(1),
}));

const EmptyCell = styled(Box)(({ theme }) => ({
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: alpha(theme.palette.background.paper, 0.5),
  borderRadius: theme.shape.borderRadius,
  border: `2px dashed ${theme.palette.divider}`,
  color: theme.palette.text.disabled,
}));

const LayoutButton = styled(ToggleButton)(({ theme }) => ({
  padding: theme.spacing(0.5, 1),
  '&.Mui-selected': {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
  },
}));

// Grid layout configurations
const LAYOUTS = {
  single: { cols: 1, rows: 1, count: 1, icon: <GridViewIcon fontSize="small" /> },
  dual: { cols: 2, rows: 1, count: 2, icon: <ViewArrayIcon fontSize="small" /> },
  quad: { cols: 2, rows: 2, count: 4, icon: <ViewModuleIcon fontSize="small" /> },
  six: { cols: 3, rows: 2, count: 6, icon: <ViewComfyIcon fontSize="small" /> },
  nine: { cols: 3, rows: 3, count: 9, icon: <GridViewIcon fontSize="small" /> },
};

/**
 * CameraGrid - Multi-camera grid display component
 *
 * @param {Object} props
 * @param {Object[]} props.devices - Array of device objects with cameras
 * @param {string} props.initialLayout - Initial layout ('single', 'dual', 'quad', 'six', 'nine')
 * @param {boolean} props.showToolbar - Show layout toolbar
 * @param {boolean} props.autoPlay - Auto-start streams
 * @param {boolean} props.compact - Compact card mode
 * @param {function} props.onDeviceClick - Callback when device is clicked
 * @param {function} props.onFullscreen - Callback for fullscreen
 * @param {Object} props.positions - Map of device positions { deviceId: position }
 * @param {Object} props.sx - Additional MUI sx props
 */
const CameraGrid = ({
  devices = [],
  initialLayout = 'quad',
  showToolbar = true,
  autoPlay = false,
  compact = false,
  onDeviceClick = null,
  onFullscreen = null,
  positions = {},
  sx = {},
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [layout, setLayout] = useState(isMobile ? 'single' : initialLayout);
  const [selectedDeviceIndex, setSelectedDeviceIndex] = useState(null);

  // Get current layout config
  const layoutConfig = useMemo(() => LAYOUTS[layout] || LAYOUTS.quad, [layout]);

  // Calculate grid items
  const gridItems = useMemo(() => {
    const items = [];
    const maxItems = layoutConfig.count;

    for (let i = 0; i < maxItems; i++) {
      items.push(devices[i] || null);
    }

    return items;
  }, [devices, layoutConfig.count]);

  // Handle layout change
  const handleLayoutChange = useCallback((event, newLayout) => {
    if (newLayout !== null) {
      setLayout(newLayout);
    }
  }, []);

  // Handle cell click for fullscreen
  const handleCellDoubleClick = useCallback((device, index) => {
    if (device) {
      setSelectedDeviceIndex(selectedDeviceIndex === index ? null : index);
      if (onFullscreen) {
        onFullscreen(device);
      }
    }
  }, [selectedDeviceIndex, onFullscreen]);

  // Calculate cell dimensions based on layout
  const getCellSize = useCallback(() => {
    const { cols, rows } = layoutConfig;
    return {
      width: `${100 / cols}%`,
      height: `${100 / rows}%`,
    };
  }, [layoutConfig]);

  // Render empty cell placeholder
  const renderEmptyCell = (index) => (
    <EmptyCell>
      <VideocamOffIcon sx={{ fontSize: 48, mb: 1, opacity: 0.5 }} />
      <Typography variant="caption">
        Sem câmera
      </Typography>
      <Typography variant="caption" color="text.disabled">
        Posição {index + 1}
      </Typography>
    </EmptyCell>
  );

  // Render single camera in fullscreen mode
  if (selectedDeviceIndex !== null && devices[selectedDeviceIndex]) {
    const device = devices[selectedDeviceIndex];
    return (
      <GridContainer sx={sx}>
        {showToolbar && (
          <GridToolbar elevation={1}>
            <Typography variant="subtitle2">
              {device.name} - Tela Cheia
            </Typography>
            <Tooltip title="Voltar para Grade">
              <IconButton
                size="small"
                onClick={() => setSelectedDeviceIndex(null)}
              >
                <ViewModuleIcon />
              </IconButton>
            </Tooltip>
          </GridToolbar>
        )}
        <GridContent>
          <Box sx={{ width: '100%', height: '100%' }}>
            <CameraCard
              device={device}
              autoPlay={true}
              showDeviceInfo={true}
              showChannelSelector={true}
              position={positions[device.id]}
              onDeviceClick={onDeviceClick}
              sx={{ height: '100%' }}
            />
          </Box>
        </GridContent>
      </GridContainer>
    );
  }

  return (
    <GridContainer sx={sx}>
      {/* Toolbar */}
      {showToolbar && (
        <GridToolbar elevation={1}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="subtitle2" color="text.secondary">
              {devices.length} câmera{devices.length !== 1 ? 's' : ''} disponível{devices.length !== 1 ? 'is' : ''}
            </Typography>
          </Box>

          <ToggleButtonGroup
            value={layout}
            exclusive
            onChange={handleLayoutChange}
            size="small"
          >
            {Object.entries(LAYOUTS).map(([key, config]) => (
              <Tooltip key={key} title={`${config.cols}x${config.rows}`}>
                <LayoutButton value={key}>
                  {config.icon}
                </LayoutButton>
              </Tooltip>
            ))}
          </ToggleButtonGroup>
        </GridToolbar>
      )}

      {/* Grid Content */}
      <GridContent>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: `repeat(${layoutConfig.cols}, 1fr)` },
            gridTemplateRows: { xs: 'auto', md: `repeat(${layoutConfig.rows}, 1fr)` },
            gap: 1,
            height: '100%',
            minHeight: { xs: 200, md: 400 },
          }}
        >
          {gridItems.map((device, index) => (
            <Fade key={device?.id || `empty-${index}`} in={true} timeout={300 + index * 100}>
              <Box
                sx={{
                  height: '100%',
                  minHeight: layoutConfig.rows === 1 ? 300 : 200,
                }}
                onDoubleClick={() => handleCellDoubleClick(device, index)}
              >
                {device ? (
                  <CameraCard
                    device={device}
                    autoPlay={autoPlay}
                    showDeviceInfo={!compact}
                    showChannelSelector={layoutConfig.count <= 4}
                    compact={compact || layoutConfig.count > 4}
                    position={positions[device.id]}
                    onDeviceClick={onDeviceClick}
                    sx={{ height: '100%' }}
                  />
                ) : (
                  renderEmptyCell(index)
                )}
              </Box>
            </Fade>
          ))}
        </Box>
      </GridContent>
    </GridContainer>
  );
};

/**
 * ChannelCell - Single channel player with JT1078 command lifecycle
 */
const ChannelCell = ({ device, channel, autoPlay }) => {
  const [streaming, setStreaming] = useState(autoPlay);
  const { requestVideo, stopVideo } = useVideoCommand();

  useEffect(() => {
    if (!device?.id || !streaming) return undefined;
    requestVideo(device.id, { channel }).catch((err) =>
      console.warn('[CameraGrid] requestVideo failed:', err));
    return () => {
      stopVideo(device.id, { channel }).catch((err) =>
        console.warn('[CameraGrid] stopVideo failed:', err));
    };
  }, [device?.id, channel, streaming, requestVideo, stopVideo]);

  if (!streaming) {
    return (
      <Box
        sx={{
          width: '100%', height: '100%', display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', bgcolor: '#000', color: '#fff',
        }}
      >
        <VideocamOffIcon sx={{ fontSize: 32, opacity: 0.3, mb: 0.5 }} />
        <Typography variant="caption" sx={{ opacity: 0.5, mb: 1 }}>Canal {channel}</Typography>
        <IconButton onClick={() => setStreaming(true)} sx={{ color: '#fff' }}>
          <PlayArrowIcon />
        </IconButton>
      </Box>
    );
  }

  return (
    <JessibucaPlayer
      deviceImei={device.uniqueId}
      channel={channel}
      autoplay
      onStop={() => setStreaming(false)}
    />
  );
};

/**
 * SingleDeviceGrid - Grid showing multiple channels from a single device
 */
export const SingleDeviceGrid = ({
  device,
  activeChannels = [],
  initialLayout = 'quad',
  autoPlay = false,
  sx = {},
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [layout, setLayout] = useState(initialLayout);
  const layoutConfig = LAYOUTS[layout] || LAYOUTS.quad;

  // Create grid items for each channel
  const gridItems = useMemo(() => {
    const items = [];
    const maxItems = layoutConfig.count;

    for (let i = 0; i < maxItems; i++) {
      const channel = activeChannels[i] || (i < 8 ? i + 1 : null);
      items.push({ channel, active: activeChannels.includes(channel) });
    }

    return items;
  }, [activeChannels, layoutConfig.count]);

  return (
    <Box sx={{ width: '100%', height: '100%', ...sx }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: `repeat(${layoutConfig.cols}, 1fr)` },
          gridTemplateRows: { xs: 'auto', md: `repeat(${layoutConfig.rows}, 1fr)` },
          gap: 1,
          height: '100%',
        }}
      >
        {gridItems.map(({ channel, active }, index) => (
          <Box key={`ch-${channel || index}`} sx={{ height: '100%', minHeight: 150 }}>
            {device && channel ? (
              <ChannelCell
                device={device}
                channel={channel}
                autoPlay={autoPlay && active}
              />
            ) : (
              <EmptyCell>
                <VideocamOffIcon sx={{ fontSize: 32, opacity: 0.3 }} />
                <Typography variant="caption">Canal {index + 1}</Typography>
              </EmptyCell>
            )}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default CameraGrid;
